import React, { useCallback, useMemo } from 'react';
import * as THREE from 'three';
import {
  FLOOR_HEIGHT,
  CELL_SIZE,
  SELECTION_BKACKOUT_OFFSET,
} from '../constants';

import Atlas from './Atlas';
import Boxes from './Boxes';
import Colors from './Colors';
import Floor from './Floor';

import RoomPlacementHighlightBox from './RoomPlacementHighlightBox';
import SelectionHighlightBox from './SelectionHighlightBox';

import { useFloorContext } from '../context/FloorContext';
import { useNFTContext } from '../context/NFTContext';
import { useTargetContext } from '../context/TargetContext';
import { useSearchContext } from '../context/SearchContext';

const width = 1000;
const height = 1000;

const FloorController = ({
  setShowLandingPage,
}: {
  setShowLandingPage: (b: boolean) => void;
}) => {
  const { target } = useTargetContext();
  const { currentFloorMap } = useFloorContext();
  const { setSelectedNFT, isNFTPlacingMode, filteredNFTs } = useNFTContext();
  const { isFiltered } = useSearchContext();

  const backgroundTextureImage = useMemo(
    () => require('../assets/background.png'),
    []
  );
  const backgroundTextureMap = useMemo(
    () =>
      new THREE.TextureLoader().load(backgroundTextureImage, (tex) => {
        tex.magFilter = THREE.NearestFilter;
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(width, height);
        tex.anisotropy = 16;
      }),
    [backgroundTextureImage]
  );
  const onResetSelectionClick = useCallback(() => {
    target === 'dev' && console.log('Reset selection click');
    setSelectedNFT(undefined);
  }, [setSelectedNFT, target]);

  if (!currentFloorMap) return null;

  return (
    <>
      {currentFloorMap && (
        <>
          <Floor
            width={currentFloorMap.width}
            height={currentFloorMap.height}
            floorHeight={FLOOR_HEIGHT}
            boxSize={CELL_SIZE}
            floorMap={currentFloorMap}
            setShowLandingPage={setShowLandingPage}
          />
          <Boxes
            width={currentFloorMap.width}
            height={currentFloorMap.height}
            boxSize={CELL_SIZE}
            floorMap={currentFloorMap}
          />
          <Atlas
            width={currentFloorMap.width}
            height={currentFloorMap.height}
            boxSize={CELL_SIZE}
            floorMap={currentFloorMap}
          />
          <Colors
            width={currentFloorMap.width}
            height={currentFloorMap.height}
            boxSize={CELL_SIZE}
            floorMap={currentFloorMap}
          />
        </>
      )}
      {/* the blackout layer for search or similar */}
      {(isFiltered || isNFTPlacingMode || filteredNFTs.length) && (
        <mesh
          position={[
            (currentFloorMap.width * CELL_SIZE) / 2,
            CELL_SIZE / 2 + SELECTION_BKACKOUT_OFFSET,
            (currentFloorMap.height * CELL_SIZE) / 2,
          ]}
          rotation={[Math.PI / 2, 0, 0]}
          scale={[100, 100, 1]}
        >
          <planeGeometry
            attach="geometry"
            args={[
              currentFloorMap.width * CELL_SIZE,
              currentFloorMap.height * CELL_SIZE,
            ]}
          />
          <meshBasicMaterial
            attach="material"
            color="#000000"
            transparent={true}
            side={THREE.DoubleSide}
            opacity={0.75}
          />
        </mesh>
      )}
      <SelectionHighlightBox />
      <RoomPlacementHighlightBox />
      {/* the cancel selection on click outside mesh */}
      <mesh
        position={[0, -FLOOR_HEIGHT * 3.25, 0]}
        rotation={[Math.PI / 2, 0, 0]}
        scale={[1, 1, 1]}
        onClick={onResetSelectionClick}
      >
        <planeGeometry
          attach="geometry"
          args={[width * CELL_SIZE, height * CELL_SIZE]}
        />
        <meshBasicMaterial
          attach="material"
          map={backgroundTextureMap}
          side={THREE.DoubleSide}
          opacity={1.0}
        />
      </mesh>
    </>
  );
};

export default FloorController;
